import { useState } from 'preact/hooks';
import { Ban, Copy, Flag, MessageCircle, UserCheck, UserPlus, UserRound, UserX, Users } from 'lucide-preact';
import type { PublicProfile, ReportRequest } from '@vm/shared';
import { api } from '../api/routes';
import { copyText } from '../platform';
import { go, openModal } from '../state/nav';
import { blockUser, inviteToParty, openChat, removeFriend, sendFriendRequest, submitReport, unblockUser } from '../state/socialActions';
import { friends, isOffline, me, restricted, toast } from '../state/store';
import { fmtCompact, fmtDuration, ratio, timeAgo } from '../ui/format';
import { MODE_INFO } from '@vm/content';
import { mapName } from '../state/content';
import { Icon } from '../ui/icons';
import { useAsync } from '../ui/hooks';
import { AsyncButton, confirmDialog, Emblem, ErrorState, LevelBadge, Modal, Spinner } from '../ui/kit';
import { CharacterPreview } from '../three/CharacterPreview';
import { MyHighlights } from './Highlights';

function StatGrid(props: { items: [string, string | number][] }) {
  return (
    <div class="stat-grid">
      {props.items.map(([label, v]) => (
        <div key={label} class="stat-cell">
          <b>{typeof v === 'number' ? fmtCompact(v) : v}</b>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function ProfileView(props: { userId: string; name?: string; close: () => void; matchId?: string }) {
  const { data, error, loading, reload } = useAsync<PublicProfile>(() => api.users.get(props.userId), [props.userId]);
  if (loading) return <Spinner label={`Loading ${props.name ?? 'profile'}…`} />;
  if (error || !data) return <ErrorState message={error ?? 'Profile unavailable'} onRetry={reload} />;
  const p = data;
  const blocked = friends.value?.blocked.some((b) => b.id === p.id) ?? false;
  const outgoing = friends.value?.outgoing.some((f) => f.id === p.id) ?? false;
  const isFriend = p.isFriend || (friends.value?.friends.some((f) => f.id === p.id) ?? false);
  const s = p.stats;
  return (
    <div class="profile">
      <div class="profile-hero">
        <div class="profile-3d">
          <CharacterPreview skin={p.cosmetics.skin} armor={p.cosmetics.armor} rotate className="profile-3d-view" />
        </div>
        <div class="profile-id">
          <div class="profile-name">
            <LevelBadge level={p.level} size="md" />
            <div>
              <h3>{p.name}</h3>
              <span class={`profile-online ${p.online ? 'on' : ''}`}>{p.online ? 'Online now' : 'Offline'}</span>
            </div>
          </div>
          {p.clan && (
            <div class="profile-clan">
              <Emblem emblem={p.clan.emblem} size={34} />
              <span>
                [{p.clan.tag}] {p.clan.name}
              </span>
            </div>
          )}
          <StatGrid
            items={[
              ['Takedowns', s.kills],
              ['K/D', ratio(s.kills, s.deaths)],
              ['Wins', s.wins],
              ['Matches', s.matches],
              ['Rating', s.rating],
              ['Voxels', s.voxelsDestroyed],
            ]}
          />
        </div>
      </div>
      {p.id !== me.value?.id && (
        <div class="profile-actions">
          {isFriend ? (
            <>
              <AsyncButton class="green" onClick={() => inviteToParty(p.id, p.name)}>
                <Users size={18} /> Invite to party
              </AsyncButton>
              {!restricted.value && (
                <button
                  class="btn"
                  onClick={() => {
                    props.close();
                    openChat(`dm:${p.id}`);
                  }}
                >
                  <MessageCircle size={18} /> Message
                </button>
              )}
              <AsyncButton
                onClick={async () => {
                  if (await confirmDialog({ title: `Remove ${p.name}?`, body: 'You can always add each other again later.', confirm: 'Remove', danger: true })) {
                    await removeFriend(p.id);
                    reload();
                  }
                }}
              >
                <UserX size={18} /> Remove friend
              </AsyncButton>
            </>
          ) : (
            !blocked && (
              <AsyncButton class="green" disabled={outgoing} onClick={() => sendFriendRequest({ name: p.name })}>
                {outgoing ? <UserCheck size={18} /> : <UserPlus size={18} />} {outgoing ? 'Request sent' : 'Add friend'}
              </AsyncButton>
            )
          )}
          {blocked ? (
            <AsyncButton onClick={() => unblockUser(p.id, p.name)}>
              <Ban size={18} /> Unblock
            </AsyncButton>
          ) : (
            <AsyncButton
              onClick={async () => {
                if (await confirmDialog({ title: `Block ${p.name}?`, body: "You won't see their chat or get their invites.", confirm: 'Block', danger: true })) await blockUser(p.id, p.name);
              }}
            >
              <Ban size={18} /> Block
            </AsyncButton>
          )}
          <button class="btn danger" onClick={() => openReport({ targetId: p.id, name: p.name, matchId: props.matchId })}>
            <Flag size={18} /> Report
          </button>
        </div>
      )}
    </div>
  );
}

export function openProfile(userId: string, name?: string, matchId?: string): void {
  if (me.value && userId === me.value.id) {
    openMyProfile();
    return;
  }
  if (isOffline.value) {
    toast("You're offline", { kind: 'warning', body: 'Profiles need a connection.' });
    return;
  }
  openModal((close) => (
    <Modal title={name ?? 'Player'} icon={<UserRound size={22} />} size="lg" onClose={close}>
      <ProfileView userId={userId} name={name} close={close} matchId={matchId} />
    </Modal>
  ));
}

function RecentMatches() {
  const res = useAsync(async () => (await api.me.matches(8)).matches, [], !isOffline.value);
  const list = res.data ?? [];
  if (isOffline.value || res.error || (!res.loading && !list.length)) return null;
  return (
    <div class="recent">
      <h4 class="section-title">Recent matches</h4>
      {res.loading ? (
        <Spinner />
      ) : (
        <div class="recent-list">
          {list.map((x) => (
            <div key={x.matchId} class={`recent-row ${x.won ? 'is-win' : ''}`}>
              <span class="recent-mode">
                <Icon name={MODE_INFO[x.mode]?.icon ?? 'swords'} size={18} />
              </span>
              <span class="recent-text">
                <b>
                  {MODE_INFO[x.mode]?.name ?? x.mode} · {mapName(x.mapId)}
                </b>
                <small>
                  {x.kills}/{x.deaths}/{x.assists} · {timeAgo(x.createdAt)}
                </small>
              </span>
              <span class={`recent-result ${x.won ? 'win' : 'loss'}`}>{x.won ? 'WIN' : x.placement > 0 ? `#${x.placement}` : 'LOSS'}</span>
              <span class="recent-gain">
                +{fmtCompact(x.xp)} XP
                <br />+{fmtCompact(x.coins)} coins
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MyProfile(props: { close: () => void }) {
  const m = me.value;
  if (!m) return null;
  const s = m.stats;
  return (
    <div class="profile">
      <div class="profile-hero">
        <div class="profile-3d">
          <CharacterPreview skin={m.cosmetics.skin} armor={m.cosmetics.armor} weapon={m.loadout.weapons.find((w) => !!w)} rotate className="profile-3d-view" />
        </div>
        <div class="profile-id">
          <div class="profile-name">
            <LevelBadge level={m.progression.level} size="md" />
            <div>
              <h3>{m.name}</h3>
              <span class="profile-online on">{m.isGuest ? 'Guest account' : 'Linked account'}</span>
            </div>
          </div>
          {m.code && (
            <button
              class="friend-code"
              onClick={async () => {
                if (await copyText(m.code)) toast('Friend code copied', { kind: 'success' });
              }}
            >
              <span>Friend code</span>
              <b>{m.code}</b>
              <Copy size={16} />
            </button>
          )}
          <StatGrid
            items={[
              ['Takedowns', s.kills],
              ['K/D', ratio(s.kills, s.deaths)],
              ['Wins', s.wins],
              ['Matches', s.matches],
              ['Headshots', s.headshots],
              ['Best streak', s.bestStreak],
              ['Voxels smashed', s.voxelsDestroyed],
              ['Voxels built', s.voxelsBuilt],
              ['Collapse KOs', s.collapseKills],
              ['Rating', s.rating],
              ['Assists', s.assists],
              ['Play time', fmtDuration(s.playtimeSec)],
            ]}
          />
        </div>
      </div>
      <MyHighlights />
      <RecentMatches />
      <div class="profile-actions">
        <button
          class="btn"
          onClick={() => {
            props.close();
            go('wardrobe');
          }}
        >
          Customize look
        </button>
        <button
          class="btn"
          onClick={() => {
            props.close();
            go('settings', { tab: 'account' });
          }}
        >
          Account settings
        </button>
      </div>
    </div>
  );
}

export function openMyProfile(): void {
  openModal((close) => (
    <Modal title="My Profile" icon={<UserRound size={22} />} size="lg" onClose={close}>
      <MyProfile close={close} />
    </Modal>
  ));
}

// Reports -------------------------------------------------------------------------------------------------------

const REASONS: { id: ReportRequest['reason']; label: string }[] = [
  { id: 'cheating', label: 'Cheating or hacking' },
  { id: 'harassment', label: 'Bullying or being mean' },
  { id: 'inappropriate_name', label: 'Bad name' },
  { id: 'inappropriate_skin', label: 'Bad skin or clan emblem' },
  { id: 'spam', label: 'Spamming' },
  { id: 'other', label: 'Something else' },
];

function ReportForm(props: { targetId: string; name: string; matchId?: string; close: () => void }) {
  const [reason, setReason] = useState<ReportRequest['reason'] | null>(null);
  const [details, setDetails] = useState('');
  const kid = restricted.value;
  return (
    <div class="report">
      <p class="muted">What did {props.name} do? Reports are private — they won't know it was you.</p>
      <div class="report-reasons">
        {REASONS.map((r) => (
          <button key={r.id} class={`report-reason ${reason === r.id ? 'is-on' : ''}`} onClick={() => setReason(r.id)}>
            {r.label}
          </button>
        ))}
      </div>
      {!kid && (
        <textarea
          class="ui-input"
          rows={3}
          maxLength={500}
          placeholder="Anything else we should know? (optional — no personal info please)"
          value={details}
          onInput={(e) => setDetails((e.currentTarget as HTMLTextAreaElement).value)}
        />
      )}
      <div class="ui-modal-actions">
        <button class="btn" onClick={props.close}>
          Cancel
        </button>
        <AsyncButton
          class="danger"
          disabled={!reason}
          onClick={async () => {
            if (!reason) return;
            const ok = await submitReport({ targetId: props.targetId, reason, details: details.trim() || undefined, matchId: props.matchId });
            if (ok) props.close();
          }}
        >
          <Flag size={18} /> Send report
        </AsyncButton>
      </div>
    </div>
  );
}

export function openReport(o: { targetId: string; name: string; matchId?: string }): void {
  openModal((close) => (
    <Modal title={`Report ${o.name}`} icon={<Flag size={22} />} size="md" onClose={close}>
      <ReportForm {...o} close={close} />
    </Modal>
  ));
}
